import {
  ACTION_IMAGE_LIST_BUTTON_CLICKED,
  CATEGORY_BUSINESS_PAGE,
} from 'utils/analytics/eventTypesConstants';
import {
  MatchParams as BusinessImageRoutesMatchParams,
  IMAGE_LIST_PATH,
} from 'components/ui/LargeImageGallery';
import {
  Dialog,
  DialogContent,
  DialogHeading,
  ImageList,
  ImageListImage,
  onClickImageArgs,
} from 'omakase';
import {
  HistorySeam,
  ImageHistory,
  getRootImageFlowPagePath,
} from 'routes/business/imageHistoryUtil';
import { Route, useHistory, useRouteMatch } from 'react-router';
import { imageListDialogContent } from 'components/ui/largeImageGalleryCssDefs';
import { joinPath } from 'utils/pathUtils';
import LightboxRoute from 'routes/business/LightboxRoute';
import React from 'react';

interface ImageListRouteProps {
  businessName: string;
  images: ImageListImage[];
  onClose: () => void;
}

const ImageListRoute: React.FunctionComponent<ImageListRouteProps> = ({
  businessName,
  images,
  onClose,
}) => {
  const match = useRouteMatch<BusinessImageRoutesMatchParams>();
  const history = useHistory<ImageHistory>();

  // Image list only opens if business has 2 or more images (most businesses have either 1 image, or >=3 images)
  if (images.length <= 1) {
    HistorySeam.replace(history, `/${match.params.businessDomain}`);
    return null;
  }

  const handleImageListClick = ({ index, id }: onClickImageArgs) => {
    window.analytics.sendEvent(CATEGORY_BUSINESS_PAGE, ACTION_IMAGE_LIST_BUTTON_CLICKED, index);
    // Note: this could also have been factored as
    //  history.push(`/${match.params.businessDomain}${IMAGE_LIST_PATH}/${id}` ...
    // but the unit test has some conniptions with correctly identifying
    // params.businessDomain in test-execution context.
    HistorySeam.push(history, joinPath(match.url, `/${id}`), {
      fromBusiness: false,
      fromImages: true,
    });
  };

  const onLightboxClose = () => {
    if (history.location.state?.fromImages) {
      HistorySeam.goBack(history);
    } else {
      const rootPath = getRootImageFlowPagePath(match.url);
      HistorySeam.replace(history, `${rootPath}/${IMAGE_LIST_PATH}`);
    }
  };

  return (
    <>
      <Dialog
        aria-label={`Images of ${businessName}`}
        open={true}
        onClose={onClose}
        variant="fullScreen"
        variantDesktop="centered"
        entranceMotion="fadeIn"
        entranceMotionDesktop="fadeIn"
        fullHeight
      >
        <DialogHeading
          onClose={onClose}
          headingConfig={{
            left: { configType: 'arrow' },
            center: { configType: 'title', title: businessName },
          }}
        />
        <DialogContent disablePadding css={imageListDialogContent}>
          <ImageList images={images} onClick={handleImageListClick} />
        </DialogContent>
      </Dialog>
      <Route path={joinPath(match.path, '/:imageId')}>
        <LightboxRoute images={images} onClose={onLightboxClose} />
      </Route>
    </>
  );
};

export default ImageListRoute;
